<template>
  <b-overlay :show="overlay" rounded="sm">
    <div v-if="orders">
      <b-card class="mb-0" no-body>
        <div class="m-2">
          <b-row>
            <b-col cols="12" class="mb-1">
              <h1>All Orders</h1>
            </b-col>

            <b-col cols="12" md="3">
              <b-form-group class="mb-2 mt-25" label="Count">
                <v-select
                    v-model="perPage"
                    :clearable="false"
                    :options="perPageOptions"
                    class="per-page-selector w-100 d-inline-block"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="3" class="mt-25">
              <b-form-group label="Search">
                <Debouncer @setValue="setQuery" />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="3">
              <b-form-group class="mb-2 mt-25" label="Order Status">
                <v-select
                    v-model="selectedStatus"
                    :options="orderStatuses"
                    :reduce="item => item.key"
                    class="per-page-selector w-100 d-inline-block"
                    label="label"
                    placeholder="Select"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="3">
              <b-form-group class="mb-2 mt-25" label="Order Type">
                <v-select
                    v-model="selectedType"
                    :options="orderTypes"
                    :reduce="item => item.value"
                    class="per-page-selector w-100 d-inline-block"
                    label="name"
                    placeholder="Select"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="3">
              <b-form-group class="mb-2 mt-25" label="Order Visibility">
                <v-select
                    v-model="selectedPrivateStatus"
                    :options="privateOrderStatusOptions"
                    :reduce="item => item.value"
                    class="per-page-selector w-100 d-inline-block"
                    label="name"
                    placeholder="Select"
                />
              </b-form-group>

              <b-form-checkbox v-model="onlyMyOrders" name="only-my-orders">
                Only assigned orders
              </b-form-checkbox>
            </b-col>
          </b-row>
        </div>

        <b-table
            ref="refUserListTable"
            :fields="myTableColumns"
            :items="orders"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            bordered
            class="position-relative orders-table"
            empty-text="Nothing found !"
            primary-key="id"
            responsive
            show-empty
            striped
        >
          <template #cell(orderCode)="data">
            <strong class="text-nowrap">
              {{ data.item.orderCode || '-' }}
            </strong>
          </template>

          <template #cell(userName)="data">
            <span class="text-nowrap">
              {{ getCustomerName(data.item) }}
            </span>
          </template>

          <template #cell(productName)="data">
            <div v-if="getProductNames(data.item).length" class="product-list">
              <div v-for="(product, index) in getProductNames(data.item)" :key="`${data.item.id}-${index}`" class="text-nowrap">
                {{ product }}
              </div>
            </div>

            <span v-else>-</span>
          </template>

          <template #cell(totalPrice)="data">
            <div class="money-cell">
              <strong>{{ formatMoney(getOrderTotalPrice(data.item), data.item.currency) }}</strong>
            </div>
          </template>

          <template #cell(adminProfit)="data">
            <div class="financial-cell">
              <strong>{{ formatMoney(getAdminPrice(data.item), data.item.currency) }}</strong>

              <small v-if="hasValue(data.item.adminPercentage)" class="text-muted">
                {{ formatPercentage(data.item.adminPercentage) }}
              </small>

              <small v-if="data.item.adminName" class="financial-email text-muted text-truncate">
                {{ data.item.adminName }}
              </small>
            </div>
          </template>

          <template #cell(platformFee)="data">
            <div class="financial-cell">
              <strong>{{ formatMoney(data.item.platformFeeAmount, data.item.currency) }}</strong>

              <small v-if="hasValue(data.item.platformFeePercentage)" class="text-muted">
                {{ formatPercentage(data.item.platformFeePercentage) }}
              </small>
            </div>
          </template>

          <template #cell(boosterProfit)="data">
            <div class="financial-cell">
              <div class="d-flex align-items-center gap-25">
                <span class="financial-label text-muted">Max:</span>
                <strong>{{ formatMoney(data.item.maxBoosterPrice, data.item.currency) }}</strong>
              </div>

              <div class="d-flex align-items-center gap-25">
                <span class="financial-label text-muted">Actual:</span>
                <strong v-if="hasPositiveValue(data.item.boosterPrice)" class="text-success">
                  {{ formatMoney(data.item.boosterPrice, data.item.currency) }}
                </strong>
                <span v-else class="text-muted">-</span>
              </div>

              <small v-if="hasValue(data.item.boosterPercentage)" class="text-muted">
                {{ formatPercentage(data.item.boosterPercentage) }}
              </small>

              <small v-if="getBoosterName(data.item)" class="financial-email text-muted text-truncate">
                {{ getBoosterName(data.item) }}
              </small>
            </div>
          </template>

          <template #cell(middleMan)="data">
            <div v-if="data.item.hasMiddleMan" class="financial-cell">
              <strong>{{ formatMoney(data.item.middleManCommissionAmount, data.item.currency) }}</strong>

              <small v-if="hasValue(data.item.middleManPercentage)" class="text-muted">
                {{ formatPercentage(data.item.middleManPercentage) }}
              </small>
            </div>

            <b-badge v-else pill variant="light-secondary">
              None
            </b-badge>
          </template>

          <template #cell(cashback)="data">
            <div class="financial-cell">
              <strong>{{ formatMoney(data.item.casheBack, data.item.currency) }}</strong>

              <small v-if="hasPositiveValue(data.item.discountCashBack)" class="text-muted">
                Discount: {{ formatMoney(data.item.discountCashBack, data.item.currency) }}
              </small>
            </div>
          </template>

          <template #cell(orderType)="data">
            <b-badge :variant="getOrderTypeMeta(data.item.orderType).variant" pill class="p-1 text-nowrap">
              {{ getOrderTypeMeta(data.item.orderType).label }}
            </b-badge>
          </template>

          <template #cell(orderStatus)="data">
            <b-badge :variant="getOrderStatusMeta(data.item.orderStatus).variant" pill class="p-1 text-nowrap">
              {{ getOrderStatusMeta(data.item.orderStatus).label }}
            </b-badge>
          </template>

          <template #cell(privateOrderStatus)="data">
            <b-badge :variant="privateOrderStatusVariant(data.item)" pill class="p-1 text-nowrap">
              {{ privateOrderStatusLabel(data.item) }}
            </b-badge>
          </template>

          <template #cell(createdAt)="data">
            <span class="text-nowrap">
              {{ formatDate(data.item.createdAt) }}
            </span>
          </template>

          <template #cell(isShowForBooster)="data">
            <div
                :id="`toggle-${data.item.id}`"
                class="d-flex align-items-center justify-content-center"
                :class="canToggleShow(data.item) ? 'cursor-pointer' : 'opacity-50'"
                role="button"
                tabindex="0"
                @click="canToggleShow(data.item) && toggleIsShow(data.item)"
                @keyup.enter="canToggleShow(data.item) && toggleIsShow(data.item)"
            >
              <feather-icon
                  v-if="data.item.isShowForBooster"
                  :class="canToggleShow(data.item) ? 'text-success' : 'text-muted'"
                  icon="CheckIcon"
                  size="20"
              />

              <feather-icon
                  v-else
                  :class="canToggleShow(data.item) ? 'text-danger' : 'text-muted'"
                  icon="XIcon"
                  size="20"
              />

              <b-tooltip v-if="!canToggleShow(data.item)" :target="`toggle-${data.item.id}`" triggers="hover" placement="top">
                Visibility can only be changed for Waiting, paid or Cancel orders.
              </b-tooltip>
            </div>
          </template>

          <template #cell(delete)="data">
            <b-link v-if="$can('manage', 'all')" v-b-modal.modal-delete @click="setSelectedOrderForDelete(data.item)">
              <feather-icon class="text-danger" icon="TrashIcon" size="20" />
            </b-link>
          </template>

          <template #cell(detail)="data">
            <b-link :to="{ name: 'apps-orders-info', params: { id: data.item.id } }">
              <feather-icon icon="EditIcon" size="20" />
            </b-link>
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col class="d-flex align-items-center justify-content-center justify-content-sm-start" cols="12" sm="6">
              <span class="text-muted">
                Total: {{ formatNumber(totalCounts) }} orders
              </span>
            </b-col>

            <b-col class="d-flex align-items-center justify-content-center justify-content-sm-end" cols="12" sm="6">
              <b-pagination
                  v-model="currentPage"
                  :per-page="perPage"
                  :total-rows="totalCounts"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>

                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>

    <b-modal id="modal-delete" cancel-title="Cancel" centered title="Delete Order" ok-title="Delete" @ok="deleteOrder">
      <span>Are You Sure You Want To Delete This Order ?</span>
    </b-modal>
  </b-overlay>
</template>

<script>
import {
  BBadge,
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BLink,
  BModal,
  BOverlay,
  BPagination,
  BRow,
  BTable,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { OrderDeleteRequest, OrderGetAllRequest, ToggleOrderIsShow } from '@/libs/Api/Order'
import Helper from '@/libs/Helper'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import Debouncer from '@/views/components/debouncer.vue'

export default {
  name: 'OrdersList',
  title: 'Orders List',

  components: {
    Debouncer,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BTable,
    BLink,
    BBadge,
    BFormCheckbox,
    BPagination,
    BOverlay,
    BModal,
    BTooltip,
    vSelect,
  },

  data() {
    return {
      orders: [],
      overlay: false,
      currentPage: 1,
      totalCounts: 0,
      perPage: 10,
      perPageOptions: [10, 20, 30, 40, 50],
      searchQuery: '',
      sortBy: 'createdAt',
      sortDesc: true,
      onlyMyOrders: false,
      isOrderHasChat: false,
      selectedStatus: null,
      selectedType: null,
      selectedPrivateStatus: null,
      IsPrivateOrder: null,
      IsCustomProduct: null,
      HasMiddleMan: null,
      selectedOrderForDelete: null,

      orderStatuses: [
        { key: 'Waiting', label: 'Waiting For Payment', variant: 'secondary' },
        { key: 'paid', label: 'Waiting For Boosters To Assign', variant: 'light-success' },
        { key: 'Doing', label: 'Booster Assigned', variant: 'success' },
        { key: 'Done', label: 'Booster Completed Order', variant: 'success' },
        { key: 'Cancel', label: 'Waiting For Reassign', variant: 'danger' },
        { key: 'Frozen', label: 'In Warranty', variant: 'info' },
        { key: 'VerifyByAdmin', label: 'Verified', variant: 'success' },
      ],

      orderTypes: [
        { name: 'Default', value: 'Default' },
        { name: 'Express', value: 'Express' },
        { name: 'Force', value: 'ForceMajor' },
      ],

      privateOrderStatusOptions: [
        { name: 'Public', value: 'Private-false' },
        { name: 'Private', value: 'Private-true Custom-un Middle-un' },
        { name: 'Middle Custom', value: 'Private-true Custom-true Middle-true' },
        { name: 'Custom', value: 'Private-true Custom-true Middle-un' },
        { name: 'Custom only', value: 'Private-true Custom-true Middle-false' },
        { name: 'Middle', value: 'Private-true Custom-un Middle-true' },
        { name: 'Middle only', value: 'Private-true Custom-false Middle-true' },
      ],

      myTableColumns: [
        { key: 'orderCode', label: 'Order Code', sortable: true },
        { key: 'productName', label: 'Service', sortable: false },
        { key: 'totalPrice', label: 'Total Price', sortable: true },
        { key: 'adminProfit', label: 'Admin Profit', sortable: false },
        { key: 'platformFee', label: 'Platform Fee', sortable: false },
        { key: 'orderType', label: 'Order Type', sortable: true },
        { key: 'orderStatus', label: 'Current Status', sortable: true },
        { key: 'createdAt', label: 'Create Date', sortable: true },
        { key: 'privateOrderStatus', label: 'Order Visibility', sortable: false },
        { key: 'isShowForBooster', label: 'Show To Boosters', sortable: false },
        { key: 'delete', label: 'Delete' },
        { key: 'detail', label: 'Detail' },
      ],
    }
  },

  async created() {
    this.applyPrivateStatusFilter(this.selectedPrivateStatus)
    await this.getOrders()
  },

  methods: {
    hasValue(value) {
      return value !== null && value !== undefined && value !== ''
    },

    hasPositiveValue(value) {
      return this.hasValue(value) && Number(value) > 0
    },

    formatNumber(value) {
      if (!this.hasValue(value) || Number.isNaN(Number(value))) return '0'

      return new Intl.NumberFormat('en-US').format(Number(value))
    },

    formatMoney(value, currency) {
      if (!this.hasValue(value) || Number.isNaN(Number(value))) return '-'

      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value))

      const symbol = this.getCurrency(currency)

      return symbol ? `${formatted} ${symbol}` : formatted
    },

    formatPercentage(value) {
      if (!this.hasValue(value) || Number.isNaN(Number(value))) return '-'

      return `${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value))}%`
    },

    formatDate(value) {
      if (!value) return '-'

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) return '-'

      return `${date.toLocaleDateString('en-US')} - ${date.toLocaleTimeString('en-US')}`
    },

    getCurrency(code) {
      if (!code) return ''

      return Helper.getCurrencySymbol(String(code).toLowerCase()) || code
    },

    getCustomerName(order) {
      return order.userName || order.buyerEmail || '-'
    },

    getBoosterName(order) {
      return order.boosterEmail || order.boosterUserName || null
    },

    getProductNames(order) {
      if (Array.isArray(order.orderItems) && order.orderItems.length) {
        return order.orderItems.map(item => item.productName).filter(Boolean)
      }

      const fallback = order.productName || order.gameServiceName || order.gameName

      return fallback ? [fallback] : []
    },

    getOrderTotalPrice(order) {
      if (this.hasValue(order.totalPrice)) return order.totalPrice
      if (this.hasValue(order.finalPrice)) return order.finalPrice

      return null
    },

    getAdminPrice(order) {
      if (this.hasValue(order.adminPrice)) return order.adminPrice
      if (this.hasValue(order.finalPrice)) return order.finalPrice

      return null
    },

    getOrderTypeMeta(type) {
      const types = {
        Default: {
          label: 'Default',
          variant: 'primary',
        },
        Express: {
          label: 'Express',
          variant: 'light-danger',
        },
        ForceMajor: {
          label: 'Force',
          variant: 'danger',
        },
      }

      return types[type] || {
        label: type || '-',
        variant: 'secondary',
      }
    },

    getOrderStatusMeta(status) {
      const item = this.orderStatuses.find(orderStatus => orderStatus.key === status)

      return item || {
        label: status || '-',
        variant: 'secondary',
      }
    },

    canToggleShow(order) {
      if (!order) return false

      return ['Waiting', 'paid', 'Cancel'].includes(order.orderStatus)
    },

    setQuery(value) {
      this.searchQuery = value || ''
      this.resetPageAndFetch()
    },

    setSelectedOrderForDelete(order) {
      this.selectedOrderForDelete = order ? { ...order } : null
    },

    normalizeBool(value) {
      if (value === true || value === false) return value
      if (value === 1 || value === 0) return Boolean(value)
      if (value === 'true') return true
      if (value === 'false') return false

      return null
    },

    getField(item, pascalKey, camelKey) {
      if (!item) return null

      if (item[pascalKey] !== undefined) return item[pascalKey]
      if (item[camelKey] !== undefined) return item[camelKey]

      return null
    },

    privateOrderStatusValue(item) {
      const rawPrivate = this.getField(item, 'IsPrivateOrder', 'isPrivateOrder')
      const rawCustom = this.getField(item, 'IsCustomProduct', 'isCustomProduct')
      const rawMiddle = this.getField(item, 'HasMiddleMan', 'hasMiddleMan')

      const isPrivate = this.normalizeBool(rawPrivate)
      const isCustom = this.normalizeBool(rawCustom)
      const hasMiddleMan = this.normalizeBool(rawMiddle)

      if (isPrivate !== true) {
        return 'Private-false'
      }

      if (isCustom === true && hasMiddleMan === true) {
        return 'Private-true Custom-true Middle-true'
      }

      if (isCustom === true && hasMiddleMan === false) {
        return 'Private-true Custom-true Middle-false'
      }

      if (isCustom === true && hasMiddleMan === null) {
        return 'Private-true Custom-true Middle-un'
      }

      if (isCustom === false && hasMiddleMan === true) {
        return 'Private-true Custom-false Middle-true'
      }

      if (isCustom === null && hasMiddleMan === true) {
        return 'Private-true Custom-un Middle-true'
      }

      return 'Private-true Custom-un Middle-un'
    },

    privateOrderStatusLabel(item) {
      const value = this.privateOrderStatusValue(item)
      const option = this.privateOrderStatusOptions.find(status => status.value === value)

      return option ? option.name : 'Private'
    },

    privateOrderStatusVariant(item) {
      const value = this.privateOrderStatusValue(item)

      const variants = {
        'Private-false': 'success',
        'Private-true Custom-un Middle-un': 'primary',
        'Private-true Custom-true Middle-true': 'danger',
        'Private-true Custom-true Middle-un': 'info',
        'Private-true Custom-true Middle-false': 'light-info',
        'Private-true Custom-un Middle-true': 'warning',
        'Private-true Custom-false Middle-true': 'dark',
      }

      return variants[value] || 'secondary'
    },

    applyPrivateStatusFilter(value) {
      this.IsPrivateOrder = null
      this.IsCustomProduct = null
      this.HasMiddleMan = null

      if (!value) return

      const filters = {
        'Private-false': {
          IsPrivateOrder: false,
        },
        'Private-true Custom-un Middle-un': {
          IsPrivateOrder: true,
        },
        'Private-true Custom-true Middle-true': {
          IsPrivateOrder: true,
          IsCustomProduct: true,
          HasMiddleMan: true,
        },
        'Private-true Custom-true Middle-un': {
          IsPrivateOrder: true,
          IsCustomProduct: true,
        },
        'Private-true Custom-true Middle-false': {
          IsPrivateOrder: true,
          IsCustomProduct: true,
          HasMiddleMan: false,
        },
        'Private-true Custom-un Middle-true': {
          IsPrivateOrder: true,
          HasMiddleMan: true,
        },
        'Private-true Custom-false Middle-true': {
          IsPrivateOrder: true,
          IsCustomProduct: false,
          HasMiddleMan: true,
        },
      }

      const selectedFilter = filters[value]

      if (!selectedFilter) return

      if (Object.prototype.hasOwnProperty.call(selectedFilter, 'IsPrivateOrder')) {
        this.IsPrivateOrder = selectedFilter.IsPrivateOrder
      }

      if (Object.prototype.hasOwnProperty.call(selectedFilter, 'IsCustomProduct')) {
        this.IsCustomProduct = selectedFilter.IsCustomProduct
      }

      if (Object.prototype.hasOwnProperty.call(selectedFilter, 'HasMiddleMan')) {
        this.HasMiddleMan = selectedFilter.HasMiddleMan
      }
    },

    buildParams() {
      const params = {
        pageNumber: this.currentPage,
        count: this.perPage,
        search: this.searchQuery,
        type: this.selectedType || '',
        status: this.selectedStatus || '',
        showMyOrder: this.onlyMyOrders,
        isOrderHasChat: this.isOrderHasChat,
      }

      if (this.IsPrivateOrder !== null) {
        params.IsPrivateOrder = this.IsPrivateOrder
      }

      if (this.IsCustomProduct !== null) {
        params.IsCustomProduct = this.IsCustomProduct
      }

      if (this.HasMiddleMan !== null) {
        params.HasMiddleMan = this.HasMiddleMan
      }

      return params
    },

    resetPageAndFetch() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }

      this.getOrders()
    },

    showToast({ title, text = '', variant = 'success', icon = null }) {
      this.$toast({
        component: ToastificationContent,
        position: 'bottom-center',
        props: {
          title,
          text,
          variant,
          icon,
        },
      })
    },

    async getOrders() {
      this.overlay = true

      const request = new OrderGetAllRequest(this)

      request.setParams(this.buildParams())

      await request.fetch(
          content => {
            this.overlay = false
            this.orders = Array.isArray(content.data.data) ? content.data.data : []
            this.totalCounts = Number(content.data.totalCount || 0)
          },
          error => {
            this.overlay = false

            this.showToast({
              title: 'Error',
              text: error.message,
              variant: 'danger',
            })
          },
      )
    },

    async deleteOrder() {
      if (!this.selectedOrderForDelete || !this.selectedOrderForDelete.id) return

      this.overlay = true

      const request = new OrderDeleteRequest(this)

      request.setParams({
        orderId: this.selectedOrderForDelete.id,
      })

      await request.fetch(
          content => {
            this.overlay = false

            if (!content.isSuccess) {
              this.showToast({
                title: 'Error',
                text: content.message,
                variant: 'danger',
              })

              return
            }

            this.selectedOrderForDelete = null

            this.showToast({
              title: 'Done',
              variant: 'success',
              icon: 'CheckIcon',
            })

            this.getOrders()
          },
          error => {
            this.overlay = false

            this.showToast({
              title: 'Error',
              text: error.message,
              variant: 'danger',
            })
          },
      )
    },

    async toggleIsShow(order) {
      if (!this.canToggleShow(order)) {
        this.showToast({
          title: 'Not Allowed',
          text: 'Visibility can only be changed for Waiting, paid or Cancel orders.',
          variant: 'warning',
        })

        return
      }

      this.overlay = true

      const request = new ToggleOrderIsShow(this)

      request.setParams({
        orderId: order.id,
      })

      await request.fetch(
          () => {
            this.overlay = false

            this.showToast({
              title: 'Success',
              text: 'Order visibility updated successfully.',
              variant: 'success',
              icon: 'CheckIcon',
            })

            this.getOrders()
          },
          error => {
            this.overlay = false

            this.showToast({
              title: 'Error',
              text: error.message,
              variant: 'danger',
            })
          },
      )
    },
  },

  watch: {
    selectedPrivateStatus(value) {
      this.applyPrivateStatusFilter(value)
      this.resetPageAndFetch()
    },

    onlyMyOrders() {
      this.resetPageAndFetch()
    },

    perPage() {
      this.resetPageAndFetch()
    },

    currentPage() {
      this.getOrders()
    },

    selectedStatus() {
      this.resetPageAndFetch()
    },

    selectedType() {
      this.resetPageAndFetch()
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.orders-table {
  font-size: 13px;
}

.cursor-pointer {
  cursor: pointer;
}

.opacity-50 {
  opacity: 0.5;
}

.money-cell {
  min-width: 110px;
  white-space: nowrap;
}

.financial-cell {
  display: flex;
  min-width: 130px;
  flex-direction: column;
  gap: 3px;
  white-space: nowrap;
}

.financial-label {
  display: inline-block;
  min-width: 45px;
  margin-right: 4px;
  font-size: 11px;
}

.financial-email {
  display: block;
  max-width: 160px;
}

.product-list {
  min-width: 150px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>